import { useRef, useState, type PointerEvent, type ReactNode } from 'react'

// Fenêtre flottante déplaçable au doigt par sa barre de titre (outils de calibration TEMPORAIRES). Sa position est
// mémorisée par la tablette et toujours ramenée dans l'écran.

interface Position {
  x: number
  y: number
}

/** Partie de la barre de titre gardée visible : la fenêtre ne peut jamais être perdue hors de l'écran. */
const keepVisiblePx = 120

function clampPosition(position: Position, viewport: { width: number; height: number }, width: number): Position {
  return {
    x: Math.min(Math.max(position.x, keepVisiblePx - width), viewport.width - keepVisiblePx),
    y: Math.min(Math.max(position.y, 0), viewport.height - 44),
  }
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight }
}

function readPosition(key: string, fallback: Position, width: number): Position {
  try {
    const raw = window.localStorage.getItem(key)
    const value = raw ? (JSON.parse(raw) as Partial<Position>) : null
    if (value && typeof value.x === 'number' && typeof value.y === 'number') {
      return clampPosition({ x: value.x, y: value.y }, viewport(), width)
    }
  } catch {
    // position par défaut
  }
  return clampPosition(fallback, viewport(), width)
}

interface Props {
  title: string
  storageKey: string
  initial: Position
  width: number
  className?: string
  label: string
  onClose: () => void
  children: ReactNode
}

export function FloatingWindow({ title, storageKey, initial, width, className, label, onClose, children }: Props) {
  const [position, setPosition] = useState(() => readPosition(storageKey, initial, width))
  const drag = useRef<{ pointerId: number; dx: number; dy: number } | null>(null)

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return
    drag.current = { pointerId: event.pointerId, dx: event.clientX - position.x, dy: event.clientY - position.y }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (current?.pointerId !== event.pointerId) return
    setPosition(clampPosition({ x: event.clientX - current.dx, y: event.clientY - current.dy }, viewport(), width))
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    drag.current = null
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(position))
    } catch {
      // position non mémorisée
    }
  }

  return (
    <div
      className={`op-float${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-label={label}
      style={{ left: `${String(position.x)}px`, top: `${String(position.y)}px`, width: `${String(width)}px` }}
    >
      <div
        className="op-float__bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span aria-hidden="true">⠿</span>
        <strong>{title}</strong>
        <button type="button" onClick={onClose}>
          Fermer
        </button>
      </div>
      <div className="op-float__body">{children}</div>
    </div>
  )
}
